import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

export interface LoginFormTheme {
  // Branding
  logo?: string;
  appName: string;
  appNameColor?: string;
  
  // Text content
  welcomeTitle?: string;
  welcomeDescription?: string;
  forgotPasswordText?: string;
  noAccountText?: string;
  signUpLinkText?: string;
  
  // Paths
  signUpPath?: string;
  resetPasswordPath?: string;
  defaultReturnPath?: string;
  
  // Additional customization
  cardClassName?: string;
  buttonClassName?: string;
  inputClassName?: string;
  logoClassName?: string;
}

export interface LoginFormProps {
  theme: LoginFormTheme;
  onLogin: (email: string, password: string) => Promise<void>;
  currentUser?: any;
  header?: React.ReactNode;
  additionalContent?: React.ReactNode;
}

export function LoginForm({
  theme,
  onLogin,
  currentUser,
  header,
  additionalContent,
}: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo || theme.defaultReturnPath || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate(returnTo);
    }
  }, [currentUser, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      {header}
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className={theme.cardClassName || "w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm"}>
          <div className="flex flex-col space-y-1.5 p-6 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center space-x-2">
              {theme.logo && (
                <img 
                  src={theme.logo} 
                  alt={`${theme.appName} Logo`} 
                  className={theme.logoClassName || "h-10 w-10"}
                />
              )}
              <span className={`text-xl font-bold ${theme.appNameColor || 'text-navy'}`}>
                {theme.appName}
              </span>
            </div>
            <h3 className="text-2xl font-semibold leading-none tracking-tight">
              {theme.welcomeTitle || 'Welcome Back'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {theme.welcomeDescription || `Log in to your ${theme.appName} account`}
            </p>
          </div>
          <div className="p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label 
                  htmlFor="email" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={theme.inputClassName || "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label 
                    htmlFor="password" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Password
                  </label>
                  {theme.resetPasswordPath && (
                    <Link
                      to={theme.resetPasswordPath}
                      className="text-sm text-primary hover:underline"
                    >
                      {theme.forgotPasswordText || 'Forgot password?'}
                    </Link>
                  )}
                </div>
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={theme.inputClassName || "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"}
                />
              </div>

              <button 
                type="submit" 
                className={theme.buttonClassName || "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 w-full"}
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Log In'}
              </button>
            </form>

            {theme.signUpPath && (
              <div className="mt-4 text-center text-sm">
                <span className="text-muted-foreground">
                  {theme.noAccountText || "Don't have an account? "}
                </span>
                <Link to={theme.signUpPath} className="text-primary hover:underline">
                  {theme.signUpLinkText || 'Sign up'}
                </Link>
              </div>
            )}

            {additionalContent}
          </div>
        </div>
      </div>
    </div>
  );
}

